import { useState, useEffect } from 'react';
import { useAPI } from '../../api';
import { useOrgContext, useOrgNavigate } from 'components/OrgContextProvider';
import { useDataCache } from 'components/DataCacheProvider';

interface UserCreateNewContainerProps {
	onChanged: () => void;
}

interface AccessGrantForm {
	tenantID: string;
	workspaceID: string | null;
	role: string;
}

interface NewUserForm {
	display: string;
	username: string;
	status: string;
	password: string;
	access: AccessGrantForm[];
}

const contextKey = (tenantID: string, workspaceID: string | null) => `${tenantID}:${workspaceID ?? ''}`;

const UserCreateNewContainer = ({ onChanged }: UserCreateNewContainerProps) => {
	const ctx = useOrgContext();
	const api = useAPI();
	const orgNavigate = useOrgNavigate();

	const [user, setUser] = useState<NewUserForm>({
		display: '',
		username: '',
		status: 'active',
		password: '',
		access: []
	});
	const { roles: rolesCache, loadRoles } = useDataCache();

	// Keep roles loaded for every tenant context currently used in the grants list.
	const grantTenantIDs = user.access.map(g => g.tenantID);
	useEffect(() => {
		[...new Set(grantTenantIDs)].forEach(tid => loadRoles(tid));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [grantTenantIDs.join(',')]);

	// Build context options for grant selectors.
	// The backend adds a synthetic { id: 'system' } tenant to availableTenants for system admins only.
	const contextOptions: { label: string; tenantID: string; workspaceID: string | null }[] = [];
	for (const tenant of ctx?.availableTenants ?? []) {
		if (tenant.id === 'system') {
			contextOptions.push({ label: 'System', tenantID: 'system', workspaceID: null });
		} else {
			contextOptions.push({ label: `Tenant: ${tenant.name}`, tenantID: tenant.id, workspaceID: null });
			for (const workspace of tenant.workspaces) {
				contextOptions.push({ label: `Workspace: ${workspace.name} (${tenant.name})`, tenantID: tenant.id, workspaceID: workspace.id });
			}
		}
	}

	const addGrant = () => {
		const tenantID = contextOptions[0].tenantID;
		loadRoles(tenantID);
		setUser(prev => ({
			...prev,
			access: [...prev.access, {
				tenantID,
				workspaceID: contextOptions[0].workspaceID,
				role: (rolesCache[tenantID] ?? [])[0]?.id ?? ''
			}]
		}));
	};

	const updateGrant = (index: number, updates: Partial<AccessGrantForm>) => {
		setUser(prev => {
			const access = [...prev.access];
			access[index] = { ...access[index], ...updates };
			return { ...prev, access };
		});
	};

	const removeGrant = (index: number) => {
		setUser(prev => ({
			...prev,
			access: prev.access.filter((_, i) => i !== index)
		}));
	};

	const createUser = () => {
		const payload: Record<string, unknown> = {
			display: user.display || user.username,
			username: user.username,
			status: user.status,
		};
		if (user.password) payload.password = user.password;
		if (user.access.length > 0) payload.access = user.access;

		api.post(`${ctx?.apiBase}/users`, payload)
		.then(() => { onChanged(); orgNavigate('/settings/users'); });
	};

	return (
		<div>
			<h3>Create User</h3>

			<table>
				<tbody>
					<tr>
						<td>Display Name</td>
						<td><input type="text" value={user.display} onChange={e => setUser({ ...user, display: e.target.value })} /></td>
					</tr>
					<tr>
						<td>Username/Email</td>
						<td><input type="text" value={user.username} onChange={e => setUser({ ...user, username: e.target.value })} /></td>
					</tr>
					<tr>
						<td>Status</td>
						<td>
							<select value={user.status} onChange={e => setUser({ ...user, status: e.target.value })}>
								<option value="active">Active</option>
								<option value="invited">Invited</option>
								<option value="inactive">Inactive</option>
							</select>
						</td>
					</tr>
					<tr>
						<td>Password</td>
						<td>
							<input
								type="password"
								value={user.password}
								placeholder="(optional)"
								onChange={e => setUser({ ...user, password: e.target.value })}
							/>
						</td>
					</tr>
					<tr>
						<td valign="top">Access Grants</td>
						<td>
							{user.access.map((grant, i) => (
								<div key={i} style={{ marginBottom: '4px' }}>
									<select
										value={contextKey(grant.tenantID, grant.workspaceID)}
										onChange={e => {
											const opt = contextOptions.find(o => contextKey(o.tenantID, o.workspaceID) === e.target.value);
											if (opt) {
												loadRoles(opt.tenantID);
												updateGrant(i, { tenantID: opt.tenantID, workspaceID: opt.workspaceID, role: '' });
											}
										}}
									>
										{contextOptions.map(opt => (
											<option
												key={contextKey(opt.tenantID, opt.workspaceID)}
												value={contextKey(opt.tenantID, opt.workspaceID)}
											>
												{opt.label}
											</option>
										))}
									</select>
									{' '}
									<select value={grant.role} onChange={e => updateGrant(i, { role: e.target.value })}>
										{(rolesCache[grant.tenantID] ?? []).map(role => (
											<option key={role.id} value={role.id}>{role.name}</option>
										))}
									</select>
									{' '}
									<button onClick={() => removeGrant(i)}>Remove</button>
								</div>
							))}
							<button onClick={addGrant}>+ Add Grant</button>
						</td>
					</tr>
					<tr>
						<td></td>
						<td><input type="button" value="Create user" onClick={createUser} /></td>
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default UserCreateNewContainer;
